import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { SetupState, SetupStep, SetupStepKey } from "@dentacore/shared-types";

import { readSetupState } from "../api/setup";
import styles from "./setup.module.css";

/**
 * Guided setup — what this clinic still needs before it can take a booking.
 *
 * **It owns no forms** (decision G2). Every one it needs was built by STORY-065
 * to 068 — the location form, the chairs panel, the hours editor, the staff
 * form, the catalogue with its CSV import — so each step here is a row saying
 * what it is, whether it is done, and a button to the real screen where it is
 * done. Duplicating those forms would put five of them in two places, and the
 * catalogue panel alone is 387 lines; the copy would drift within a story or
 * two, and the wrong one would be the one a new clinic met.
 *
 * That is also why *leave and come back* needed no code. There is nothing to
 * leave: the work happens on the screens that already own it, and this page
 * recounts from scratch every time it is opened.
 *
 * **Four steps are required and hours are not** (G1, following H2). Hours move
 * where the day grid's axis opens and closes and refuse nothing, so a branch
 * that has never had them is already bookable. The row is offered, carries no
 * tick-count, and is never counted (no "Optional" tag since 2026-10-18).
 */
export function SetupPage(): React.JSX.Element {
  const [state, setState] = useState<SetupState | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    readSetupState(controller.signal)
      .then(setState)
      .catch(() => {
        if (controller.signal.aborted) return;
        setFailed(true);
      });

    return () => controller.abort();
  }, []);

  if (failed) {
    return (
      <div className={styles.page}>
        <p className={styles.quiet} data-testid="setup-failed">
          Could not read what this clinic still needs. Reload to try again.
        </p>
      </div>
    );
  }

  if (state === null) {
    return (
      <div className={styles.page}>
        <p className={styles.quiet}>Reading&hellip;</p>
      </div>
    );
  }

  /**
   * Where the chairs, procedures and hours steps point.
   *
   * **Not an assumption that a clinic has one location.** Those three steps are
   * clinic-wide questions answered per branch, so a single row cannot name a
   * branch; it opens the first one as a starting point, and the locations list
   * below is where a clinic with several picks the one it means. `undefined` is
   * the honest answer for a clinic with none, and the row then renders
   * unlinked rather than pointing somewhere that does not exist.
   */
  const firstLocation = state.locations[0]?.branchId;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title} data-testid="setup-page">
          Set up your clinic
        </h1>
        <p className={styles.lead}>
          Four things, and you can book. Each one opens the screen where you enter it, and this page
          recounts every time you come back &mdash; so you can do them in any order, or stop halfway
          and pick it up tomorrow.
        </p>

        <div
          className={state.ready ? `${styles.verdict} ${styles.verdictReady}` : styles.verdict}
          data-testid="setup-verdict"
          data-ready={state.ready}
        >
          <p className={styles.verdictTitle}>
            {state.ready ? "Ready to book" : "Not ready to book yet"}
          </p>
          <p className={styles.verdictBody}>{verdictBody(state)}</p>

          <p className={styles.count} data-testid="setup-count">
            {state.requiredStepsDone} of {state.requiredStepsTotal} done
          </p>
          <div className={styles.track} aria-hidden="true">
            {state.steps
              .filter((s) => s.required)
              .map((s) => (
                <span
                  key={s.key}
                  className={s.done ? `${styles.tick} ${styles.tickDone}` : styles.tick}
                />
              ))}
          </div>
        </div>
      </header>

      <section className={styles.block}>
        <div className={styles.blockHead}>
          <h2 className={styles.blockTitle}>The steps</h2>
        </div>

        <ul className={styles.list}>
          {state.steps.map((step) => (
            <li key={step.key}>
              <StepRow step={step} branchId={firstLocation} />
            </li>
          ))}
        </ul>
      </section>

      {/*
        Level two, and the reason `/setup` stays reachable after day one (G4).
        A clinic opening its second location gets this same page telling it what
        that location still lacks — which a checklist that deleted itself once
        the first location was ready could not do.
      */}
      {state.locations.length > 0 && (
        <section className={styles.block}>
          <div className={styles.blockHead}>
            <h2 className={styles.blockTitle}>Your locations</h2>
            <p className={styles.blockNote}>
              Chairs and the procedure catalogue belong to a location, not to the clinic, so each
              one needs its own. Opening hours are listed here because they decide where the
              calendar&rsquo;s day starts and ends &mdash; they never stop a booking, so a location
              with none is still bookable.
            </p>
          </div>

          <ul className={styles.list}>
            {state.locations.map((location) => (
              <li key={location.branchId}>
                <Link
                  to={`/settings/locations/${location.branchId}`}
                  className={styles.row}
                  data-testid="setup-location"
                >
                  <span
                    className={location.ready ? `${styles.mark} ${styles.markDone}` : styles.mark}
                    aria-hidden="true"
                  >
                    {location.ready ? "✓" : ""}
                  </span>
                  <span className={styles.rowBody}>
                    <span className={styles.rowTitle}>
                      <span className={styles.rowName}>{location.name}</span>
                      {location.ready ? (
                        <span className={styles.tagGood} data-testid="location-ready">
                          Bookable
                        </span>
                      ) : (
                        <span className={styles.tagWarm} data-testid="location-not-ready">
                          Not bookable yet
                        </span>
                      )}
                      {!location.hasHours && (
                        <span className={styles.tagQuiet} data-testid="location-no-hours">
                          No hours set
                        </span>
                      )}
                    </span>
                    <span className={styles.rowMeta}>
                      {plural(location.chairCount, "chair", "chairs")} &middot;{" "}
                      {plural(location.procedureCount, "procedure", "procedures")}
                    </span>
                  </span>
                  <span className={styles.chevron} aria-hidden="true">
                    ›
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** What each step is, and which screen actually does it. */
const STEPS: Record<
  SetupStepKey,
  { name: string; blurb: string; to: (branchId: string | undefined) => string | null }
> = {
  locations: {
    name: "Open a location",
    blurb: "Where you work. A clinic can run any number, and each keeps its own rooms and prices.",
    to: () => "/settings/locations",
  },
  chairs: {
    name: "Add a chair",
    blurb: "Each chair becomes a column on the calendar. One is enough to start.",
    /*
      **The Chairs pane, not the location's page.** Chairs stopped being a
      section of a location's page when Settings became a rail — that page now
      holds the location's own details, and would send somebody here looking for
      a chair one more click. The pane shows whichever location is active, and
      the rail's switcher is beside it; a clinic at this step has usually just
      opened its first.
    */
    to: () => "/settings/chairs",
  },
  providers: {
    name: "Add a dentist or hygienist",
    /*
      **Says the step waits on them, not on you** (Anthony's QA, item 9).

      It read "They get a link to set their own password", which describes the
      invite and not its consequence: the step counts clinicians who can sign
      in, so it stays unticked until the invite is accepted. Somebody who had
      just added a dentist read an unticked step and a blurb that mentioned no
      such condition.
    */
    blurb:
      "Whoever performs the treatment. They get a link to set their own password — this step " +
      "ticks once one of them has used it, because a clinician who cannot sign in cannot be " +
      "booked against.",
    to: () => "/people/staff",
  },
  procedures: {
    name: "Enter your procedures",
    blurb: "Codes, durations and prices. Type them, import a CSV, or copy another location's list.",
    /* Under Treatment plans since PL1 — out of Settings, same active location. */
    to: () => "/plans/procedures",
  },
  hours: {
    name: "Set your opening hours",
    blurb:
      "They decide where the calendar's day starts and ends, and nothing else — a booking outside them is never refused.",
    to: (branchId) => (branchId === undefined ? null : `/settings/locations/${branchId}`),
  },
};

function StepRow({
  step,
  branchId,
}: {
  step: SetupStep;
  branchId: string | undefined;
}): React.JSX.Element {
  const meta = STEPS[step.key];
  const to = meta.to(branchId);

  const body = (
    <>
      <span
        className={step.done ? `${styles.mark} ${styles.markDone}` : styles.mark}
        aria-hidden="true"
      >
        {step.done ? "✓" : ""}
      </span>
      <span className={styles.rowBody}>
        <span className={styles.rowTitle}>
          <span className={styles.rowName}>{meta.name}</span>
          {step.done && step.required && (
            <span className={styles.tagGood} data-testid="step-done">
              Done
            </span>
          )}
        </span>
        {/*
          `missing` names the locations that lack it, rather than only counting
          them — the acceptance check's "reports honestly what is still
          missing". When there is nothing to report the step describes itself.
        */}
        <span className={styles.rowMeta} data-testid={`step-meta-${step.key}`}>
          {step.missing ?? meta.blurb}
        </span>
      </span>
      {to !== null && (
        <span className={styles.chevron} aria-hidden="true">
          ›
        </span>
      )}
    </>
  );

  // A step whose screen does not exist yet — the chairs of a clinic with no
  // location — is shown and not linked, rather than linking somewhere useless.
  if (to === null) {
    return (
      <span className={styles.rowStatic} data-testid={`step-${step.key}`} data-done={step.done}>
        {body}
      </span>
    );
  }

  return (
    <Link to={to} className={styles.row} data-testid={`step-${step.key}`} data-done={step.done}>
      {body}
    </Link>
  );
}

/**
 * The honest sentence under the verdict.
 *
 * The awkward case is real and worth naming: every required step can be
 * satisfied *somewhere* while no single location has both a chair and a
 * catalogue, and a clinic told "4 of 4 done" that still cannot book would
 * rightly call the screen a liar.
 */
function verdictBody(state: SetupState): string {
  if (state.ready) {
    return state.readyLocationCount === state.locations.length
      ? "Every location has a chair and a procedure, and you have someone to perform it. Open the calendar and book."
      : `${state.readyLocationCount} of ${state.locations.length} locations can take a booking. The rest are listed below with what they still need.`;
  }

  if (state.requiredStepsDone === state.requiredStepsTotal) {
    return "Each step is done somewhere, but no single location has both a chair and a procedure — so there is nowhere to book yet. The locations below say which is which.";
  }

  const outstanding = state.steps.filter((s) => s.required && !s.done).length;
  return `${outstanding} ${outstanding === 1 ? "step" : "steps"} to go. Nothing here is permanent — you can change any of it later from Settings.`;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}
